/** @format */

import {ColumnType, TanStackTableData} from 'components/Table/core/types';

import {sanitizeFileName} from 'helpers/fileHelpers';

import {formatDate, translateKey} from './formatHelpers';

const extractValuesForExcel = (jsonValue: unknown): {key: string; values: string[]}[] => {
  if (!jsonValue) return [];

  let parsedValue;
  try {
    parsedValue = typeof jsonValue === 'string' ? JSON.parse(jsonValue) : jsonValue;
  } catch {
    return [{key: '', values: [String(jsonValue)]}];
  }

  if (!parsedValue || typeof parsedValue !== 'object') {
    return [{key: '', values: [String(parsedValue)]}];
  }

  const result: {key: string; values: string[]}[] = [];

  Object.entries(parsedValue as Record<string, unknown>).forEach(([key, value]) => {
    const values: string[] = [];

    if (Array.isArray(value)) {
      if (value.length > 0) {
        values.push(...value.map(item => String(item)));
      }
    } else if (value !== null && value !== undefined && String(value) !== '') {
      values.push(String(value));
    }

    if (values.length > 0) {
      result.push({key, values});
    }
  });

  return result;
};

const formatValueForExcel = (value: unknown): string => {
  if (!value) return '';

  const extractedData = extractValuesForExcel(value);

  if (extractedData.length === 0) return '';

  return extractedData
    .map(({key, values}) => {
      const translatedKey = translateKey(key);
      return `${translatedKey}: ${values.join(', ')}`;
    })
    .join('\n');
};

interface ExcelColumnLabels {
  valueBefore: string;
  valueAfter: string;
}

interface ExportToExcelParams {
  data: TanStackTableData[];
  columns: ColumnType[];
  companyName: string;
  startDate: string;
  endDate: string;
  excelColumnLabels: ExcelColumnLabels;
}

const formatCompositeCell = (name: unknown, id: unknown): string => {
  const nameStr = name ? String(name) : '';
  const idStr = id ? String(id) : '';

  if (nameStr && idStr) return `${nameStr} (${idStr})`;
  if (nameStr) return nameStr;
  if (idStr) return `(${idStr})`;
  return '';
};

const buildHeaderMap = (columns: ColumnType[]): Map<string, string> => {
  const map = new Map<string, string>();
  for (const col of columns) {
    map.set(col.id, String(col.title));
  }
  return map;
};

const getHeader = (headerMap: Map<string, string>, columnId: string): string => {
  return headerMap.get(columnId) ?? columnId;
};

const mapRowToExcelData = (
  row: TanStackTableData,
  headerMap: Map<string, string>,
  excelColumnLabels: ExcelColumnLabels,
): Record<string, string> => {
  return {
    [getHeader(headerMap, 'time')]: formatDate(String(row.time || '')),
    [getHeader(headerMap, 'accountId')]: String(row.accountId || ''),
    [getHeader(headerMap, 'campaignId')]: formatCompositeCell(row.campaignName, row.campaignId),
    [getHeader(headerMap, 'adGroupId')]: formatCompositeCell(row.adGroupName, row.adGroupId),
    [getHeader(headerMap, 'adId')]: formatCompositeCell(row.adName, row.adId),
    [getHeader(headerMap, 'categoryDescription')]: String(row.categoryDescription || ''),
    [getHeader(headerMap, 'typeDescription')]: String(row.typeDescription || ''),
    [excelColumnLabels.valueBefore]: formatValueForExcel(row.valueBefore),
    [excelColumnLabels.valueAfter]: formatValueForExcel(row.valueAfter),
  };
};

export const exportTableToExcel = async ({
  data,
  columns,
  companyName,
  startDate,
  endDate,
  excelColumnLabels,
}: ExportToExcelParams): Promise<void> => {
  if (data.length === 0) return;

  const XLSX = await import('xlsx');

  const headerMap = buildHeaderMap(columns);
  const excelData = data.map(row => mapRowToExcelData(row, headerMap, excelColumnLabels));

  const worksheet = XLSX.utils.json_to_sheet(excelData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Changelog');

  const safeCompanyName = sanitizeFileName(companyName);
  const fileName = `changelog_${safeCompanyName}_${startDate}_${endDate}.xlsx`;

  XLSX.writeFile(workbook, fileName);
};
